<template>
  <b-container fluid class="m-0 p-0">
    <!-- Testa di sezione: titolo e "nuova patologia" -->
    <div class="section-head section-head--stack mb-3">
      <h2 class="section-title mb-0">{{ $t('PatologieComponent.title') }}</h2>
      <div class="filter-bar__actions ml-auto">
        <b-button
          v-if="user.permessi.includes('alimentare scheda paziente') || user.type === 0"
          variant="primary"
          class="section-head__action"
          @click="$bvModal.show('new-pathology')"
        >
          <b-icon icon="plus" aria-hidden="true"></b-icon> {{ $t('PatologieComponent.new') }}
        </b-button>
      </div>
    </div>
    <div v-if="pathologies && pathologies.length === 0" class="empty-state">
      <b-icon icon="bookmark" class="empty-state__icon" aria-hidden="true"></b-icon>
      <p>{{ $t('PatologieComponent.nessuna') }}</p>
    </div>
    <!-- Una riga per patologia: il clic la seleziona, la freccia apre il
         dettaglio; sotto alla riga scelta compare "crea terapia" -->
    <div v-else class="list-stack">
      <div v-for="(pat, ind) in pathologies" :key="ind">
        <div
          class="list-row point"
          :class="{
            'list-row--active':
              selectedPathology && pat.hashid === selectedPathology.hashid,
          }"
          @click="selectPathology(pat, false, ind)"
          @keydown.enter.prevent="selectPathology(pat, false, ind)"
          @keydown.space.prevent="selectPathology(pat, false, ind)"
          role="button"
          tabindex="0"
          :aria-label="$t('PatologieComponent.seleziona_patologia') + ': ' + pat.name"
        >
          <span class="list-row__icon" :title="pat.tipo">
            <b-icon :icon="pathologyTypesIcons[pat.type]" aria-hidden="true"></b-icon>
          </span>
          <div class="list-row__body">
            <div class="list-row__title">
              {{ pat.name }}
              <b-badge v-if="pat.is_primary" variant="primary" class="text-uppercase" pill>{{ $t('PatologieComponent.primaria') }}</b-badge>
            </div>
            <div class="list-row__meta">
              <span class="text-capitalize">{{ pat.tipo }}</span>
              <span v-if="pat.start_at && !pat.end_at">
                · {{$t('PatologieComponent.data_inizio')}}: {{ formatDataOnlyNumbers(pat.start_at) }}
              </span>
              <span v-else-if="pat.start_at">
                · {{$t('PatologieComponent.dal')}} {{ formatDataOnlyNumbers(pat.start_at) }}
              </span>
              <span v-if="pat.end_at">
                {{$t('PatologieComponent.al')}} {{ formatDataOnlyNumbers(pat.end_at) }}
              </span>
            </div>
          </div>
          <div class="list-row__actions">
            <b-button
              variant="white"
              class="btn-icon btn-sm"
              :title="$t('PatologieComponent.vedi')"
              :aria-label="$t('PatologieComponent.vedi_dettaglio') + ': ' + pat.name"
              @click.stop="selectPathology(pat, true, ind)"
              @keydown.enter.stop
              @keydown.space.stop
            >
              <b-icon icon="chevron-right" aria-hidden="true"></b-icon>
            </b-button>
          </div>
        </div>
        <div
          v-if="
            selectedPathology &&
            pat.hashid === selectedPathology.hashid &&
            ($route.name == 'profilo sanitario' ||
              $route.name == 'scheda paziente') &&
            $route.path != '/cittadino/profilosanitario/terapie/nuova'
          "
          class="list-row-detail d-flex justify-content-end"
        >
          <b-button type="button" size="sm" variant="secondary" @click="goToNewTherapy"
            >{{ $t('PatologieComponent.create') }} {{ selectedPathology.name }}</b-button
          >
        </div>
      </div>
    </div>
    <b-modal
      size="xl"
      id="new-pathology"
      centered
      hide-footer
      :title="$t('PatologieComponent.new')"
    >
      <b-form @submit.prevent="onSubmit">
        <div class="modal-search">
          <h3 class="section-eyebrow mb-2">{{ $t('PatologieComponent.searchTitle') }}</h3>
            <b-form-group
              label=""
              class="mb-0"
              :description=" $t('PatologieComponent.searchDescription')"
            >
              <div class="search-field">
                <b-form-input
                  v-capitalize-first
                  type="search"
                  name="search-pathology"
                  :placeholder=" $t('PatologieComponent.searchPlaceholder')"
                  v-model="filters.term"
                  v-debounce:500ms="doSearch"
                  :debounce-events="'keyup'"
                >
                </b-form-input>
                <b-icon icon="search" class="search-field__icon" aria-hidden="true"></b-icon>
              </div>
            </b-form-group>
            <b-form-group
              v-if="results.length > 0"
              :label="$t('PatologieComponent.selectPatologia')"
              label-for="pathology-name"
              class="text-left mt-3 mb-0"
            >
              <b-form-select
                v-capitalize-first-select
                v-if="searchType > 0"
                class="my-3"
                name="pathology-name"
                v-model="form.name"
              >
                <b-form-select-option
                  v-for="(res, ind) in results"
                  :key="ind"
                  :value="res.valore"
                  >{{ res.valore }}
                </b-form-select-option>
                <b-form-select-option :value="null">-- </b-form-select-option>
              </b-form-select>
              <b-form-select
                v-capitalize-first-select
                v-else
                class="my-3"
                name="name"
                v-model="icdSelected"
              >
                <b-form-select-option
                  v-for="(res, ind) in results"
                  :key="ind"
                  :value="res"
                  >{{ res.full_code }} {{ res.name }} ({{
                    res.category_name
                  }})</b-form-select-option
                >
                <b-form-select-option :value="null">-- </b-form-select-option>
              </b-form-select>
            </b-form-group>
        </div>
        <div v-if="form.name || notFound">
          <b-row cols="1" class="text-left mx-auto mt-3">
            <b-col>
              <b-form-group class="text-left" :label="$t('PatologieComponent.nomePatologia')">
                <b-form-input
                  v-capitalize-first
                  class="col-12"
                  v-model="form.name"
                  type="text"
                  required
                  placeholder="..."
                >
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col>
              <!-- <p class="mt-2">Seleziona la tipologia*</p> -->
              <b-form-group :label="$t('PatologieComponent.tipo')" class="mt-3">
                <b-form-radio-group
                  v-model="form.type"
                  name="type"
                  class="d-flex my-3"
                >
                  <b-form-radio
                    v-for="(tipo, ind) in types"
                    :key="ind"
                    :value="tipo.value"
                    >{{ tipo.label }}
                  </b-form-radio>
                </b-form-radio-group>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row
            cols="1"
            cols-md="2"
            class="text-left mx-auto mt-3"
            v-if="form.type < 2"
          >
            <b-col>
              <b-form-group
                :label="$t('PatologieComponent.inizio')"
                label-for="start_at"
                class="text-left mx-auto"
              >
                <b-form-input
                  class="my-3"
                  name="start_at"
                  type="date"
                  v-model="form.start_at"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col v-if="form.type === 1">
              <b-form-group
                :label="$t('PatologieComponent.fine')"
                label-for="end_at"
                class="text-left mx-auto"
              >
                <b-form-input
                  class="my-3"
                  name="end_at"
                  type="date"
                  v-model="form.end_at"
                  :state="form.start_at == null ? null : validDate"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row cols="1" class="text-left mx-auto mt-3">
            <b-col class="" v-if="form.own && user.type === 0">
              <b-form-group
                label=""
                :description="$t('PatologieComponent.visibilitaDescrizione')"
                label-for="private"
                class="text-left"
              >
                <b-form-checkbox
                  id="private"
                  v-model="form.private"
                  name="private"
                  variant="text-secondary"
                  :value="true"
                  :unchecked-value="false"
                >
                  {{$t('PatologieComponent.visibileSoloAMe')}}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col class="mt-2">
              <b-form-group
                :label="$t('PatologieComponent.annotazioni')"
                label-for="details"
                class="text-left mt-2"
              >
                <b-form-textarea
                  v-capitalize-first
                  class=" "
                  name="details"
                  type="text"
                  v-model="form.details"
                ></b-form-textarea>
              </b-form-group>
            </b-col>
            <b-col class="mt-2">
              <b-form-group
                :label="$t('PatologieComponent.priorita')"
                class="text-left"
              >
                <b-form-checkbox
                  v-model="form.is_primary"
                  name="is_primary"
                  switch
                  :value="true"
                  :unchecked-value="false"
                >
                  {{ $t('PatologieComponent.impostaPrimaria') }}
                </b-form-checkbox>
                <small class="text-muted" v-if="form.is_primary">{{ $t('PatologieComponent.impostaPrimariaHelp') }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="d-flex justify-content-end mt-3">
            <b-button
              type="button"
              variant="popup-button"
              class="mr-2"
              @click.prevent="handleAnnulla"
              >{{ $t('annulla') }}</b-button
            >
            <b-button
              type="submit"
              :disabled="form.name == null || validDate === false"
              variant="popup-button"
              >{{ $t('salva') }}
            </b-button>
          </div>
        </div>
      </b-form>
    </b-modal>
    <b-modal
      size="xl"
      id="edit-pathology"
      centered
      hide-footer
      :title="selectedPathology ? selectedPathology.name : ''"
    >
      <b-form class="pt-2">
        <div v-if="selectedPathology">
          <b-row cols="1" class="text-left mx-auto mt-3">
            <b-col v-if="!noEdit" class="modal-search">
              <h3 class="section-eyebrow mb-2">{{ $t('PatologieComponent.searchTitle') }}</h3>
              <b-form-group
                label=""
                class="mb-0"
                :description=" $t('PatologieComponent.searchDescription')"
              >
                <div class="search-field">
                  <b-form-input
                    v-capitalize-first
                    name="search-pathology"
                    type="search"
                    :placeholder=" $t('PatologieComponent.searchPlaceholder')"
                    v-model="filters.term"
                    v-debounce:500ms="doSearch"
                    :debounce-events="'keyup'"
                  >
                  </b-form-input>
                  <b-icon icon="search" class="search-field__icon" aria-hidden="true"></b-icon>
                </div>
              </b-form-group>
              <b-form-group
                v-if="results.length > 0"
                :label="$t('PatologieComponent.selectPatologia')"
                label-for="pathology-name"
                class="text-left mt-3 mb-0"
              >
                <b-form-select
                  v-capitalize-first-select
                  v-if="searchType > 0"
                  class="my-3"
                  name="pathology-name"
                  v-model="selectedPathology.name"
                >
                  <b-form-select-option
                    v-for="(res, ind) in results"
                    :key="ind"
                    :value="res.valore"
                    >{{ res.valore }}
                  </b-form-select-option>
                  <b-form-select-option :value="null">-- </b-form-select-option>
                </b-form-select>
                <b-form-select
                  v-capitalize-first-select
                  v-else
                  class="my-3"
                  name="name"
                  v-model="icdSelected"
                >
                  <b-form-select-option
                    v-for="(res, ind) in results"
                    :key="ind"
                    :value="res"
                    >{{ res.full_code }} {{ res.name }}({{
                      res.category_name
                    }})</b-form-select-option
                  >
                  <b-form-select-option :value="null">-- </b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group :label="$t('PatologieComponent.nomePatologia')" class="text-left">
                <b-form-input
                  v-capitalize-first
                  class="col-12"
                  v-model="selectedPathology.name"
                  type="text"
                  required
                  placeholder="..."
                  :readonly="noEdit"
                ></b-form-input>
              </b-form-group>
            </b-col>

            <b-col v-if="noEdit && selectedPathology.classificazione">
              <p class="text-left pb-1 pt-3">
                {{ $t('PatologieComponent.codicePatologia') }}
                {{ selectedPathology.classificazione }}
              </p>
            </b-col>
            <b-col class="mt-3">
              <p>{{ $t('PatologieComponent.tipo') }} {{ !noEdit ? "*" : "" }}</p>
              <b-form-radio-group
                v-model="selectedPathology.type"
                name="type"
                class="d-flex my-3"
                :readonly="noEdit"
                :disabled="noEdit"
              >
                <b-form-radio
                  v-for="(tipo, ind) in types"
                  :key="ind"
                  :value="tipo.value"
                  >{{ tipo.label }}
                </b-form-radio>
              </b-form-radio-group>
            </b-col>
          </b-row>
          <b-row
            cols="1"
            cols-md="2"
            class="text-left mx-auto mt-3"
            v-if="selectedPathology.type < 2"
          >
            <b-col>
              <b-form-group
                :label="$t('PatologieComponent.inizio')"
                label-for="start_at"
                class="text-left mx-auto"
              >
                <b-form-input
                  class="my-3"
                  name="start_at"
                  type="date"
                  v-model="selectedPathology.start_at"
                  :readonly="noEdit"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col v-if="selectedPathology.type === 1">
              <b-form-group
                :label="$t('PatologieComponent.fine')"
                label-for="end_at"
                class="text-left mx-auto"
              >
                <b-form-input
                  class="my-3"
                  name="end_at"
                  type="date"
                  v-model="selectedPathology.end_at"
                  :readonly="noEdit"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row cols="1" class="text-left mx-auto mt-3">
            <b-col class="" v-if="selectedPathology.own && user.type === 0">
              <b-form-group
                label=""
                :description="$t('PatologieComponent.visibilitaDescrizione')"
                label-for="private"
                class="text-left"
              >
                <b-form-checkbox
                  id="private"
                  v-model="selectedPathology.private"
                  name="private"
                  :value="1"
                  :unchecked-value="0"
                  :readonly="noEdit"
                  :disabled="noEdit"
                >
                  {{$t('PatologieComponent.visibileSoloAMe')}}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col class="mt-2">
              <b-form-group
                :label="$t('PatologieComponent.annotazioni')"
                label-for="details"
                class="text-left mt-2"
              >
                <b-form-textarea
                  v-capitalize-first
                  class=" "
                  name="details"
                  type="text"
                  v-model="selectedPathology.details"
                  :readonly="noEdit"
                ></b-form-textarea>
              </b-form-group>
            </b-col>
            <b-col class="mt-2">
              <b-form-group
                :label="$t('PatologieComponent.priorita')"
                class="text-left"
              >
                <b-form-checkbox
                  v-model="selectedPathology.is_primary"
                  name="is_primary"
                  switch
                  :value="true"
                  :unchecked-value="false"
                  :disabled="noEdit"
                >
                  {{ $t('PatologieComponent.impostaPrimaria') }}
                </b-form-checkbox>
                <small class="text-muted" v-if="selectedPathology.is_primary && !noEdit">{{ $t('PatologieComponent.impostaPrimariaHelp') }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="d-flex justify-content-end mt-3" v-if="noEdit">
            <b-button
              type="button"
              size="sm"
              variant="outline-secondary"
              @click.prevent="noEdit = false"
              >{{ $t('PatologieComponent.modifica') }}</b-button
            >
          </div>
          <div class="mt-3" v-if="!noEdit">
            <p class="list-row__meta text-left">{{ $t('PatologieComponent.campiObbligatori') }}</p>
            <div class="d-flex justify-content-end">
              <b-button
                type="button"
                variant="popup-button"
                class="mr-2"
                @click.prevent="handleAnnulla"
                >{{ $t('annulla') }}</b-button
              >
              <b-button
                type="button"
                @click.prevent="doUpdate"
                :disabled="selectedPathology.name == null"
                variant="popup-button"
                >{{ $t('salva') }}
              </b-button>
            </div>
          </div>
          <p class="list-row__meta text-left text-capitalize-first mt-3 mb-0">
            {{ $t('PatologieComponent.inseritaIl') }} {{ formatData(selectedPathology.created_at) }}
          </p>
        </div>
      </b-form>
    </b-modal>
  </b-container>
</template>
<script>
import * as moment from "moment/moment";
import { utilityService, cittadinoService } from "@/_services";
import { mapState, mapActions } from "vuex";
export default {
  name: "PatologieComponent",
  props: ["subject"],
  data() {
    return {
      pathologyTypesIcons: ["calendar4", "lightning", "brightness-high"],
      filters: {
        term: null,
      },
      searchType: 0,
      results: [],
      form: {
        name: null,
        type: 0,
        start_at: null,
        end_at: null,
        details: null,
        own: true,
        private: false,
        is_primary: false,
        subjectable_id: null,
        subjectable_type: null,
      },
      notFound: false,
      pathologies: null,
      // typesColors: ["ter-4", "ter-1", "ter-2"],
      selectedPathology: null,
      noEdit: true,
      patIndex: null,
      icdSelected: null,
    };
  },
  computed: {
    ...mapState({
      alert: (state) => state.alert,
    }),
    ...mapState("utente", ["status", "user"]),
    validDate() {
      if (this.form.end_at == null || this.form.end_at == "") {
        return null;
      }
      return moment(this.form.start_at).isSameOrBefore(moment(this.form.end_at))
        ? null
        : false;
    },
    types() {
      return [
        { value: 0, label: this.$t('PatologieComponent.cronica') },
        { value: 1, label: this.$t('PatologieComponent.acuta') },
        { value: 2, label: this.$t('PatologieComponent.stagionale') },
      ]
    }
  },
  watch: {
    icdSelected: {
      handler(val) {
        // console.log(val);
        if (this.noEdit === true) {
          this.form.name = val.name;
          // this.form.name = val.name + "(" + val.category_name + ")";
          this.form.icd10_pathology_id = val.hashid;
        } else if (this.selectedPathology) {
          this.selectedPathology.name = val.name;
          // val.name + "(" + val.category_name + ")";
          this.selectedPathology.classificazione_id = val.hashid;
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      clearAlert: "alert/clear",
      errorAlert: "alert/error",
      successAlert: "alert/success",
      infoAlert: "alert/info",
    }),
    searchIcd() {
      var self = this;
      utilityService
        .searchPatologiaIcd(this.filters)
        .then(function (response) {
          self.results = response.data.data;
        })
        .catch(function (error) {
          console.log(error);
          self.results = [];
        });
    },
    formatData(data) {
      return moment(data).format("DD MMM YYYY");
    },
    formatDataOnlyNumbers(data) {
      return moment(data).format("DD/MM/YYYY");
    },
    doSearch() {
      if (this.searchType === 0) {
        this.searchIcd();
      } else if (this.searchType === 1) {
        this.searchCronica();
      } else {
        this.searchRara();
      }
    },
    searchRara() {
      var self = this;
      utilityService
        .searchPatologiaRara(this.filters)
        .then(function (response) {
          self.results = response.data.resultObj;
        })
        .catch(function (error) {
          console.log(error);
          self.results = [];
        });
    },
    searchCronica() {
      var self = this;
      utilityService
        .searchPatologiaCronica(this.filters)
        .then(function (response) {
          self.results = response.data.resultObj;
        })
        .catch(function (error) {
          console.log(error);
          self.results = [];
        });
    },
    selectPathology(pathology, doShow, ind) {
      this.selectedPathology = pathology;
      if (doShow) {
        this.$bvModal.show("edit-pathology");
      }
      this.patIndex = ind;
      this.$emit("selected-pathology", pathology);
    },
    onSubmit() {
      var self = this;
      cittadinoService
        .storePathology(this.form)
        .then(function (response) {
          self.successAlert(self.$t('PatologieComponent.successSave'));
          self.$bvModal.hide("new-pathology");
          self.fetchPathologies();
          self.clearForm();
        })
        .catch(function (error) {
          var msg = (self.$t('PatologieComponent.failSave'));
          console.log(error);
          self.errorAlert(msg);
          self.$bvModal.hide("new-pathology");
        });
    },
    doUpdate() {
      var self = this;
      cittadinoService
        .updatePathology(this.selectedPathology)
        .then(function (response) {
          self.successAlert(self.$t('PatologieComponent.successUpdate'));
          self.$bvModal.hide("edit-pathology");
          self.fetchPathologies();
          self.clearForm();
          self.noEdit = true;
        })
        .catch(function (error) {
          var msg = (self.$t('PatologieComponent.failUpdate'));
          console.log(error);
          self.errorAlert(msg);
          self.$bvModal.hide("edit-pathology");
          self.noEdit = true;
        });
    },
    fetchPathologies() {
      if (this.subject) {
        return this.fetchUserPathologies();
      }
      this.fetchOwnPathologies();
    },
    fetchOwnPathologies() {
      var self = this;
      cittadinoService
        .getPathologies()
        .then(function (response) {
          self.pathologies = self.sortPathologies(response.data.data);
          if (self.$route.query.pax != undefined) {
            var ndx = parseInt(self.$route.query.pax);
            self.hasPathology = 1;
            self.selectPathology(response.data.data[ndx], false, ndx);
          }
        })
        .catch(function (error) {
          self.errorAlert(self.$t('PatologieComponent.failFetch'));;
        });
    },
    fetchUserPathologies() {
      var self = this;
      var filters = {
  user: this.subject.hashid,
  subject_type: this.subject.type,
      };
      cittadinoService
        .getPathologies(filters)
        .then(function (response) {
          self.pathologies = self.sortPathologies(response.data.data);
          if (self.$route.query.pax != undefined) {
            var ndx = parseInt(self.$route.query.pax);
            self.hasPathology = 1;
            self.selectPathology(response.data.data[ndx], false, ndx);
          }
        })
        .catch(function (error) {
          self.errorAlert(self.$t('PatologieComponent.failFetch'));
        });
    },
    clearForm() {
      this.form.name = null;
      this.form.details = null;
      this.filters.term = null;
      this.form.start_at = null;
      this.form.end_at = null;
      this.form.is_primary = false;
      this.notFound = false;
    },
    handleAnnulla() {
      if (!this.noEdit) {
        this.noEdit = true;
        this.selectedPathology = null;
        return this.$bvModal.hide("edit-pathology");
      }
      this.$bvModal.hide("new-pathology");
      this.clearForm();
    },
    sortPathologies(list) {
      if (!Array.isArray(list)) return list;
      return list.slice().sort((a, b) => {
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;
        return 0;
      });
    },
    goToNewTherapy() {
      var destination = {
        path: "/cittadino/profilosanitario/terapie/nuova",
        query: {
          pax: this.patIndex,
        },
      };
      if (this.subject) {
        destination = {
          path: "/operatore/pazienti/terapia",
          query: {
            sub: this.subject.hashid,
            type: this.subject.type,
            pax: this.patIndex,
          },
        };
      }
      this.$router.push(destination);
    },
  },
  created() {
    if (this.subject) {
      this.form.subjectable_id = this.subject.hashid;
      this.form.subjectable_type = this.subject.type;
      this.form.own = false;
    }
    this.fetchPathologies();
  },
  mounted() {
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      // console.log("Modal is about to be shown", bvEvent, modalId);
      this.clearForm();
    });
  },
};
</script>
